import React, { useEffect, useMemo, useRef } from 'react';
import './common-dimension-table-view.css';
import { useFormikContext } from 'formik';
import { observer } from 'mobx-react';
import {
  arrayToObject,
  normalizeDimensionsByColumn,
  transformDimensionData,
} from '../../../../../../helpers/array-to-tree-helper';
import { filterTableDataBySelected } from '../../../../../../helpers/column-utils';
import { RemoveIcon } from '../../../../../../shared-components/svgs/svgs';
import { formatMessage } from '../../../../../../translations/format-message';
import { useStore } from '../../../../../../contexts/store-provider';

const CommonDimensionTable = (props: any) => {
  const DIMENSION_CELL_MAX_LENGTH = 250;
  const { values, setFieldValue } = useFormikContext<any>();
  const { productStore } = useStore();
  const { getDimensionOrder, getDimensionTableColumnKeys } = productStore;
  const lastRowRef = useRef<HTMLTableRowElement>(null);

  const dedupeSelectedColumns = (columns: any[] = []) => {
    const seen = new Set<string>();
    return columns.filter((col: any) => {
      const id = col?.id ?? col?.value ?? col?.data?.Id ?? '';
      const label = String(col?.label || col?.data?.Messages || '')
        .trim()
        .toLowerCase();
      const key = `${id}__${label}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const getDimensionColumnOrder = () => {
    const masterOrder = Array.isArray(getDimensionTableColumnKeys)
      ? getDimensionTableColumnKeys
        .map((col: any) => Number(col?.Id || 0))
        .filter((id: number) => id > 0)
      : [];

    return masterOrder.length > 0 ? masterOrder : getDimensionOrder || [];
  };

  const orderSelectedColumns = (columns: any[] = []) => {
    const dedupedColumns = dedupeSelectedColumns(columns);
    const order = getDimensionColumnOrder();
    if (!order || order.length === 0) return dedupedColumns;

    return [...dedupedColumns].sort((a: any, b: any) => {
      const indexA = order.indexOf(Number(a?.id || a?.value || a?.data?.Id || 0));
      const indexB = order.indexOf(Number(b?.id || b?.value || b?.data?.Id || 0));

      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
  };

  const syncDimensionTableData = (nextTableData: any[]) => {
    setFieldValue(`Dimension.TableData`, nextTableData);

    (values?.Process || []).forEach((process: any, processIndex: number) => {
      if (Number(process?.ProcessId) <= 0) return;

      const processColumns = orderSelectedColumns(
        process?.DimensionDetails?.SelectedColumns || []
      );
      if (processColumns.length === 0) return;

      setFieldValue(
        `Process.${processIndex}.DimensionDetails.TableData`,
        filterTableDataBySelected({
          SelectedColumns: processColumns,
          TableData: nextTableData,
        })
      );
    });
  };

  const selectedColumns = useMemo(() => {
    const selectedProcessId = Number(values?.SelectedProcessForDimensionTable);
    const fallbackProcess =
      values?.Process?.find((p: any) => Number(p?.ProcessId) === selectedProcessId) ||
      values?.Process?.find((p: any) => Number(p?.ProcessId) > 0);

    const processColumns =
      fallbackProcess?.DimensionDetails?.SelectedColumns || [];

    const fallbackDimensionRows =
      fallbackProcess?.fileIndices?.find((fi: any) => {
        const isDimension =
          fi?.IsDimension === true ||
          String(fi?.IsDimension).toLowerCase() === 'true';
        return (
          isDimension &&
          Array.isArray(fi?.DimensionsData) &&
          fi.DimensionsData.length > 0
        );
      })?.DimensionsData || [];

    const fallbackColumns =
      normalizeDimensionsByColumn(fallbackDimensionRows).map((dd: any) => ({
        id: Number(dd?.DimensionId || -1),
        value: Number(dd?.DimensionId || -1),
        label: dd?.DimensionColumn || '',
        disabled: false,
        data: {
          Id: Number(dd?.DimensionId || -1),
          Messages: dd?.DimensionColumn || '',
          MessagesType: dd?.DimensionType || 0,
        },
      })) || [];

    const defaultColumnsFromKeys =
      (getDimensionTableColumnKeys || []).map((col: any) => ({
        id: Number(col?.Id || -1),
        value: Number(col?.Id || -1),
        label: col?.Messages || '',
        disabled: false,
        data: col,
      })) || [];

    const columns =
      values?.Dimension?.SelectedColumns?.length > 0
        ? values?.Dimension?.SelectedColumns
        : processColumns?.length > 0
        ? processColumns
        : fallbackColumns?.length > 0
        ? fallbackColumns
        : defaultColumnsFromKeys;
    return orderSelectedColumns(columns);
  }, [values, getDimensionOrder, getDimensionTableColumnKeys]);

  useEffect(() => {
    const currentColumns = values?.Dimension?.SelectedColumns || [];
    if (!Array.isArray(currentColumns) || currentColumns.length === 0) return;

    const orderedColumns = orderSelectedColumns(currentColumns);
    const currentKey = currentColumns.map((col: any) => col?.id).join(',');
    const orderedKey = orderedColumns.map((col: any) => col?.id).join(',');

    if (currentKey !== orderedKey) {
      setFieldValue('Dimension.SelectedColumns', orderedColumns);
    }
  }, [getDimensionOrder, getDimensionTableColumnKeys, setFieldValue, values?.Dimension?.SelectedColumns]);

  const tableData = useMemo(() => {
    const rootTable = values?.Dimension?.TableData || [];
    const hasRootColumns = values?.Dimension?.SelectedColumns?.length > 0;
    if (rootTable.length > 0) {
      return rootTable.some((row: any) => row?.DimensionId || row?.DimensionColumn)
        ? transformDimensionData(normalizeDimensionsByColumn(rootTable))
        : rootTable;
    }
    if (hasRootColumns) return [];

    const selectedProcessId = Number(values?.SelectedProcessForDimensionTable);
    const fallbackProcess =
      values?.Process?.find((p: any) => Number(p?.ProcessId) === selectedProcessId) ||
      values?.Process?.find((p: any) => Number(p?.ProcessId) > 0);

    const processTable = fallbackProcess?.DimensionDetails?.TableData || [];
    const hasProcessColumns =
      fallbackProcess?.DimensionDetails?.SelectedColumns?.length > 0;
    if (processTable.length > 0) {
      return processTable.some((row: any) => row?.DimensionId || row?.DimensionColumn)
        ? transformDimensionData(normalizeDimensionsByColumn(processTable))
        : processTable;
    }
    if (hasProcessColumns) return [];

    const fallbackDimensionRows =
      fallbackProcess?.fileIndices?.find((fi: any) => {
        const isDimension =
          fi?.IsDimension === true ||
          String(fi?.IsDimension).toLowerCase() === 'true';
        return (
          isDimension &&
          Array.isArray(fi?.DimensionsData) &&
          fi.DimensionsData.length > 0
        );
      })?.DimensionsData || [];

    return transformDimensionData(normalizeDimensionsByColumn(fallbackDimensionRows));
  }, [values]);

  // Auto-scroll to the last row when new rows are added
  useEffect(() => {
    if (lastRowRef.current && tableData?.length > 0) {
      lastRowRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [tableData?.length]);

  // Add new empty row
  const addNewRow = () => {
    const newRow: any = arrayToObject(
      selectedColumns?.map((e: any) => e?.label) || [],
      ''
    );
    // setFieldValue(
    //   `Process.${selectedProcessIndex}.DimensionDetails.TableData`,
    //   [...tableData, newRow]
    // );
    syncDimensionTableData([...tableData, newRow]);
  };

  // Handle cell value change
  const handleCellChange = (rowIndex: number, header: any, value: string) => {
    // Prevent entering # character
    const filteredValue = value
      .replace(/#/g, '')
      .slice(0, DIMENSION_CELL_MAX_LENGTH);
    const updatedRows = tableData.map((row: any, index: number) =>
      index === rowIndex ? { ...row, [header]: filteredValue } : row
    );
    syncDimensionTableData(updatedRows);
  };

  // Delete row
  const deleteRow = (rowIndex: number) => {
    const updatedRows = tableData.filter((_, index) => index !== rowIndex);
    syncDimensionTableData(updatedRows);
  };

  return (
    <div className="dynamic-table-container">
      {selectedColumns?.length > 0 && (
        <button
          type="button"
          className="btn btn-primary ad-new-row"
          onClick={addNewRow}
        >
          + {formatMessage('add_new_row_dimension')}
        </button>
      )}
      <div className="tableDevices tableDesign dimensionTableNewRow">
        <div className="table-responsive">
          <div className="react-bootstrap-table">
            <table className="table">
              <thead>
                <tr>
                  <th className=""></th>
                  {(selectedColumns?.map((e: any) => e?.label) || [])?.map(
                    (header, index) => <th key={index}>{header}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {tableData?.map((row: any, rowIndex: number) => (
                  <tr
                    key={rowIndex}
                    ref={rowIndex === tableData.length - 1 ? lastRowRef : null}
                    className="editable-row"
                  >
                    <td className="action-column">
                      <button
                        type="button"
                        className="btn btn-light-danger-new btn-sm mt-2"
                        onClick={() => deleteRow(rowIndex)}
                        title="Delete row"
                      >
                        <RemoveIcon />
                      </button>
                    </td>
                    {(selectedColumns?.map((e: any) => e?.label) || [])?.map(
                      (header, colIndex) => (
                        <td key={colIndex}>
                          {(() => {
                            const cellValue = String(row?.[header] ?? '');
                            const isMaxLengthReached =
                              cellValue.length >= DIMENSION_CELL_MAX_LENGTH;
                            return (
                              <>
                          <textarea
                                value={cellValue}
                            onChange={(e) =>
                              handleCellChange(rowIndex, header, e.target.value)
                            }
                            // placeholder={`${formatMessage('enter')} ${header}`}
                                className={`editable-input form-control ${isMaxLengthReached ? 'is-invalid' : ''
                                  }`}
                                maxLength={DIMENSION_CELL_MAX_LENGTH}
                          />
                                {isMaxLengthReached && (
                                  <div className="invalid-feedback d-block errorMessage">
                                    {formatMessage('max_process_limit_reached')}
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </td>
                      ))}
                  </tr>
                ))}
                {tableData?.length === 0 && (
                  <tr className="empty-row">
                    <td
                      colSpan={(selectedColumns?.length || 0) + 1}
                      className="empty-message text-center w-100"
                    >
                      {formatMessage('no_data_available')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="d-flex flex-row-reverse mt-2 pe-2">
        <p>
          <small>{formatMessage('total_rows')}:</small>{' '}
          <strong>{tableData?.length}</strong>
        </p>
      </div>
    </div>
  );
};
export default observer(CommonDimensionTable);
